import React from 'react';
import LedgerTable from '../../../common/ledgerTable/LedgerTable';
import { LEDGER_ROW_ACTION } from '../../../common/ledgerTable/ledgerTable.constants';
import styles from './styles/transaction-history-card.module.scss';

/**
 * "Transaction History" card (Figma 5747:1812). Uses the same shell
 * (`SectionCard`) and table variant (`TableVariants.HISTORY`) as the
 * Team page so the user-journey page matches the rest of the admin
 * visually. Table chrome uses the shared `history-table-chrome` mixin
 * (same Figma reference as Team / Reports HISTORY tables).
 *
 * The columns themselves live in the shared `LedgerTable` (transaction type,
 * advance type, amount, fee, created, processed, destination account, actions)
 * so any other surface that lists ledger rows renders them identically — and
 * so does the empty-state copy: the table tells a failed fetch, a filter that
 * matched nothing and an empty ledger apart, with a retry on the first and a
 * way out of the second.
 *
 * Pagination, sorting and filtering are all server-side: `pageInfo` carries
 * `{ current, perPage, total }` from the backend and `onTableChange` re-fetches
 * whatever slice the user asked for. `sort` and `typeFilter` are controlled, so
 * the headers always reflect the ordering the server actually applied.
 *
 * @param {Object} props
 * @param {Array<Object>} [props.rows]
 * @param {boolean} [props.loading]
 * @param {{ current: number, perPage: number, total?: number } | null} [props.pageInfo]
 * @param {(pagination, filters, sorter, extra) => void} [props.onTableChange]
 * @param {{ sortBy: string, sortDir: string } | null} [props.sort]
 * @param {string|null} [props.typeFilter]
 * @param {string|null} [props.timezone] the zone the ledger endpoint declared
 *   for this payload; the table renders and labels in it
 * @param {boolean} [props.failed] the last fetch failed, as opposed to
 *   returning nothing — the shared table owns the copy for both
 * @param {() => void} [props.onRetry] re-fetches the slice the URL describes
 * @param {(record: Object) => void} [props.onOpenTicket]
 * @param {boolean} [props.canAction]
 */
function TransactionHistoryCard({
    rows,
    loading,
    pageInfo,
    onTableChange,
    sort,
    typeFilter,
    timezone = null,
    failed = false,
    onRetry,
    onOpenTicket,
    canAction = true,
}) {
    return (
        <LedgerTable
            rows={rows}
            loading={loading}
            pageInfo={pageInfo}
            onChange={onTableChange}
            sort={sort}
            typeFilter={typeFilter}
            timezone={timezone}
            rowAction={LEDGER_ROW_ACTION.OPEN_TICKET}
            onRowAction={onOpenTicket}
            canAction={canAction}
            failed={failed}
            onRetry={onRetry}
            // A member whose advances are all filtered out by the Type dropdown
            // has not "no transactions" — the table says the filter matched
            // nothing instead, so support does not go looking for a data problem.
            hasActiveFilters={!!typeFilter}
            classNames={{ shell: styles.tableShell, root: styles.tableRoot }}
        />
    );
}

export default TransactionHistoryCard;
